/**
 * My papers.
 *
 * A table, not a grid of cards. A teacher with thirty papers wants to scan a list
 * and open one; cards would show six of them on a screen and make the other
 * twenty-four a scroll.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useMemo, useRef, useState } from 'react'
import { api, saveBlob } from '../../lib/api/client'
import type { Template, WorksheetSummary } from '../../lib/api/client'
import {
  Button,
  Dialog,
  EmptyState,
  PageHeader,
  Pill,
  Spinner,
  useTabList,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { STATUS_TONE } from '../../components/statusTone'
import { toast } from '../../components/toast'
import { useAuth } from '../../app/useAuth'
import { formatLocalDate, formatLocalDateTime, parseApiTimestamp } from '../../lib/dates'
import { SHEET_TYPES, sheetTypeLabel, type SheetType } from '../../lib/sheetTypes'
import './papers.css'

type PaperTab = 'all' | 'archive' | SheetType
type SortKey = 'title' | 'sheet_type' | 'status' | 'question_count' | 'updated_at'
type SortDir = 'asc' | 'desc'
type SortState = { key: SortKey; dir: SortDir } | null
const ARCHIVABLE_STATUSES = new Set(['DRAFT', 'CHANGES_REQUESTED'])

/** Default sort: latest edits first. */
const DEFAULT_SORT: SortState = { key: 'updated_at', dir: 'desc' }

export function Papers() {
  const tabList = useTabList()
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [chooserOpen, setChooserOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<PaperTab>('all')
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [archiving, setArchiving] = useState<WorksheetSummary | null>(null)

  const papers = useQuery({
    queryKey: ['worksheets', 'active'],
    queryFn: () => api.listWorksheets(false),
  })
  const archivedPapers = useQuery({
    queryKey: ['worksheets', 'archive'],
    queryFn: () => api.listWorksheets(true),
  })
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => api.templates() })
  const retention = useQuery({
    queryKey: ['retention', 'school', 'current'],
    queryFn: () => api.schoolRetention(),
  })

  const refreshPapers = () => queryClient.invalidateQueries({ queryKey: ['worksheets'] })

  const create = useMutation({
    mutationFn: (template: Template) =>
      api.createWorksheet(
        `Untitled ${template.name}`,
        template.sheet_type,
        template.id,
      ),
    onSuccess: (created) => {
      setChooserOpen(false)
      navigate(`/papers/${created.id}`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const archive = useMutation({
    mutationFn: api.archiveWorksheet,
    onSuccess: (paper) => {
      setArchiving(null)
      void refreshPapers()
      toast(`"${paper.title}" moved to Archive.`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const restore = useMutation({
    mutationFn: api.restoreWorksheet,
    onSuccess: (paper) => {
      void refreshPapers()
      toast(`"${paper.title}" restored to My papers.`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const duplicate = useMutation({
    mutationFn: api.duplicateWorksheet,
    onSuccess: () => refreshPapers(),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const exportPaper = useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const blob = await api.exportWorksheetJson(id)
      saveBlob(blob, `${title.replace(/[/\\:*?"<>|]+/g, '_')}.json`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const importInputRef = useRef<HTMLInputElement>(null)
  const importPaper = useMutation({
    mutationFn: async (file: File) => api.importWorksheetJson(await file.text()),
    onSuccess: (result) => {
      void refreshPapers()
      if (result.notes.length > 0) {
        toast(result.notes.join(' '), 'error')
      } else {
        toast(`"${result.worksheet.title}" imported.`)
      }
      navigate(`/papers/${result.worksheet.id}`)
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const rows = papers.data ?? []
  const archiveRows = archivedPapers.data ?? []
  const availableSheetTypes = new Set<SheetType>([
    ...rows.map((paper) => paper.sheet_type),
    ...(templates.data ?? []).map((template) => template.sheet_type),
  ])
  const tabs: Array<{ value: PaperTab; label: string; count: number }> = [
    { value: 'all', label: 'All papers', count: rows.length },
    ...SHEET_TYPES.filter((type) => availableSheetTypes.has(type.value)).map((type) => ({
      value: type.value,
      label: type.plural,
      count: rows.filter((paper) => paper.sheet_type === type.value).length,
    })),
    { value: 'archive', label: 'Archive', count: archiveRows.length },
  ]
  const unsortedRows =
    activeTab === 'archive'
      ? archiveRows
      : activeTab === 'all'
        ? rows
        : rows.filter((paper) => paper.sheet_type === activeTab)

  const visibleRows = useMemo(() => {
    if (!sort) return unsortedRows
    const { key, dir } = sort
    const sorted = [...unsortedRows].sort((a, b) => {
      let cmp = 0
      if (key === 'title') {
        cmp = a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
      } else if (key === 'sheet_type') {
        cmp = (a.sheet_type_label ?? a.sheet_type).localeCompare(
          b.sheet_type_label ?? b.sheet_type,
          undefined,
          { sensitivity: 'base' },
        )
      } else if (key === 'status') {
        cmp = a.status.localeCompare(b.status)
      } else if (key === 'question_count') {
        cmp = a.question_count - b.question_count
      } else if (key === 'updated_at') {
        const aVal = parseApiTimestamp(a.updated_at)?.getTime() ?? 0
        const bVal = parseApiTimestamp(b.updated_at)?.getTime() ?? 0
        cmp = aVal - bVal
      }
      return dir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [unsortedRows, sort])

  // Keep every hook above loading-state returns. The queries resolve after the
  // first render, so calling useMemo only once loading finished changed the
  // component's hook count and made React crash the entire page.
  if (papers.isLoading || archivedPapers.isLoading) {
    return <Spinner label="Loading your papers" />
  }

  const toggleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      // Third click: back to default (latest edits first)
      return DEFAULT_SORT
    })
  }

  const activeLabel = tabs.find((tab) => tab.value === activeTab)?.label ?? 'All papers'
  const examTab = activeTab === 'unit_test' || activeTab === 'annual_exam'
  const archiveTab = activeTab === 'archive'

  return (
    <div className="tc-page tc-papers">
      <PageHeader
        eyebrow="Teaching workspace"
        title="My papers"
        icon={<Icon.papers size={20} />}
        description={
          rows.length === 0
            ? `Good to see you, ${user?.first_name}. Start your first paper when you are ready.`
            : `${rows.length} paper${rows.length === 1 ? '' : 's'} across your active paper types.`
        }
        actions={
          <>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) importPaper.mutate(file)
            event.currentTarget.value = ''
          }}
        />
        <Button
          disabled={importPaper.isPending}
          onClick={() => importInputRef.current?.click()}
        >
          Import paper
        </Button>
        <Button
          variant="primary"
          icon={<Icon.plus size={16} />}
          disabled={create.isPending}
          onClick={() => setChooserOpen(true)}
        >
          New paper
        </Button>
          </>
        }
      />

      {rows.length === 0 && archiveRows.length === 0 ? (
        <EmptyState
          icon={<Icon.papers size={20} />}
          title="No papers yet"
          message="Start one and the preview beside the editor shows exactly what will print."
          action={
            <Button variant="primary" onClick={() => setChooserOpen(true)}>
              Make your first paper
            </Button>
          }
        />
      ) : (
        <div className="tc-paper-tabs-shell">
          <div className="tc-paper-tabs" ref={tabList.ref} onKeyDown={tabList.onKeyDown} role="tablist" aria-label="Paper types">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                id={`paper-tab-${tab.value}`}
                className={`tc-paper-tab ${activeTab === tab.value ? 'is-active' : ''}`}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.value}
                aria-controls="paper-tab-panel"
                onClick={() => { setActiveTab(tab.value); setSort(DEFAULT_SORT) }}
              >
                <span>{tab.label}</span>
                <span className="tc-paper-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>

          <section
            id="paper-tab-panel"
            className="tc-paper-panel"
            role="tabpanel"
            aria-labelledby={`paper-tab-${activeTab}`}
          >
            {examTab && (
              <div className="tc-paper-panel-note">
                <Icon.warning size={15} />
                Answers are hidden for all {activeLabel.toLowerCase()}.
              </div>
            )}
            {archiveTab && (
              <div className="tc-paper-panel-note tc-archive-note">
                <Icon.archive size={15} />
                Papers here can be restored until their deletion date.
              </div>
            )}
            {visibleRows.length === 0 ? (
              <div className="tc-paper-tab-empty">
                <h2>{archiveTab ? 'Archive is empty' : `No ${activeLabel.toLowerCase()} yet`}</h2>
                <p>
                  {archiveTab
                    ? 'Papers you archive will remain recoverable here until their deletion date.'
                    : 'Create a paper, then choose its sheet type from the editor.'}
                </p>
              </div>
            ) : (
              <div className="tc-paper-table-wrap">
              <table className="tc-table tc-paper-table">
                <thead>
                  <tr>
                    <SortHeader label="Name" sortKey="title" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Type" sortKey="sheet_type" sort={sort} onSort={toggleSort} style={{ width: 120 }} />
                    <SortHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} style={{ width: 140 }} />
                    <SortHeader label="Questions" sortKey="question_count" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
                    <SortHeader label={archiveTab ? 'Archived' : 'Edited'} sortKey="updated_at" sort={sort} onSort={toggleSort} style={{ width: 140 }} />
                    {archiveTab && <th style={{ width: 150 }}>Deletes</th>}
                    <th style={{ width: archiveTab ? 110 : 350 }}>
                      <span className="tc-sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((paper) => (
                    <tr key={paper.id}>
                      <td data-label="Name">
                        {archiveTab ? (
                          <strong className="tc-archived-paper-name">{paper.title}</strong>
                        ) : (
                          <button
                            className="tc-linkish"
                            onClick={() => navigate(`/papers/${paper.id}`)}
                          >
                            {paper.title}
                          </button>
                        )}
                      </td>
                      <td data-label="Type">
                        <Pill tone="muted">{sheetTypeLabel(paper.sheet_type)}</Pill>
                      </td>
                      <td data-label="Status">
                        <Pill tone={STATUS_TONE[paper.status] ?? 'muted'}>
                          {paper.status_label}
                        </Pill>
                      </td>
                      <td data-label="Questions">{paper.question_count}</td>
                      <td data-label={archiveTab ? 'Archived' : 'Edited'} className="tc-cell-muted">
                        {formatLocalDateTime(archiveTab ? paper.archived_at : paper.updated_at)}
                      </td>
                      {archiveTab && (
                        <td data-label="Deletes">
                          <span className="tc-purge-date">{formatLocalDate(paper.purge_after)}</span>
                        </td>
                      )}
                      <td data-label="Actions">
                        <div className="tc-paper-actions">
                          {archiveTab ? (
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={restore.isPending}
                              onClick={() => restore.mutate(paper.id)}
                            >
                              Restore
                            </Button>
                          ) : (
                            <>
                              <Button size="sm" onClick={() => navigate(`/papers/${paper.id}`)}>
                                Open
                              </Button>
                              <Button size="sm" onClick={() => duplicate.mutate(paper.id)}>
                                Duplicate
                              </Button>
                              <Button
                                size="sm"
                                disabled={exportPaper.isPending}
                                onClick={() =>
                                  exportPaper.mutate({ id: paper.id, title: paper.title })
                                }
                              >
                                Export
                              </Button>
                              <Button
                                size="sm"
                                icon={<Icon.archive size={14} />}
                                disabled={!ARCHIVABLE_STATUSES.has(paper.status)}
                                title={
                                  ARCHIVABLE_STATUSES.has(paper.status)
                                    ? 'Move this paper to Archive'
                                    : 'A paper with the reviewer or printing team cannot be archived.'
                                }
                                onClick={() => setArchiving(paper)}
                              >
                                Archive
                              </Button>
                              {!ARCHIVABLE_STATUSES.has(paper.status) && (
                                <span className="tc-disabled-action-note">Drafts only</span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </section>
        </div>
      )}

      {archiving && (
        <Dialog
          open
          title={`Archive “${archiving.title}”?`}
          onClose={() => !archive.isPending && setArchiving(null)}
          footer={
            <>
              <Button disabled={archive.isPending} onClick={() => setArchiving(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={archive.isPending}
                icon={<Icon.archive size={15} />}
                onClick={() => archive.mutate(archiving.id)}
              >
                {archive.isPending ? 'Archiving…' : 'Move to Archive'}
              </Button>
            </>
          }
        >
          <div className="tc-archive-confirmation">
            <span className="tc-archive-confirmation-icon" aria-hidden="true">
              <Icon.archive size={22} />
            </span>
            <div>
              <strong>This paper will not be deleted immediately.</strong>
              <p>
                It will move to Archive and remain recoverable for{' '}
                <strong>{retention.data?.retention_days ?? 14} days</strong>. After that, it is
                permanently deleted.
              </p>
            </div>
          </div>
        </Dialog>
      )}

      <Dialog
        open={chooserOpen}
        title="What kind of paper are you creating?"
        onClose={() => setChooserOpen(false)}
        className="tc-new-paper-dialog"
      >
        <p className="tc-new-paper-intro">
          Choose a paper type prepared by your school administrator. Its logo,
          header fields and page details will be applied automatically.
        </p>

        {templates.isLoading ? (
          <Spinner label="Loading your school's paper types" />
        ) : (templates.data ?? []).length === 0 ? (
          <div className="tc-new-paper-empty">
            <strong>No paper types are ready yet.</strong>
            <span>Ask your school administrator to create a Worksheet or Exam Paper header.</span>
          </div>
        ) : (
          <div className="tc-new-paper-types">
            {(templates.data ?? []).map((template) => {
              const exam = template.sheet_type !== 'worksheet'
              const PaperIcon = exam ? Icon.review : Icon.papers
              return (
                <button
                  key={template.id}
                  type="button"
                  className="tc-new-paper-type"
                  disabled={create.isPending}
                  onClick={() => create.mutate(template)}
                >
                  <span className={`tc-new-paper-icon ${exam ? 'is-exam' : ''}`}>
                    <PaperIcon size={23} />
                  </span>
                  <span className="tc-new-paper-copy">
                    <strong>{template.name}</strong>
                    <span>{exam ? 'Exam paper · marks included' : 'Worksheet'}</span>
                    <small>{templateFieldLabels(template)}</small>
                  </span>
                  <span className="tc-new-paper-arrow" aria-hidden="true"><Icon.chevronRight size={18} /></span>
                </button>
              )
            })}
          </div>
        )}
      </Dialog>
    </div>
  )
}

function templateFieldLabels(template: Template) {
  const labels = (template.fields ?? [])
    .map((field) => String(field.label ?? '').trim())
    .filter(Boolean)
  return labels.length ? labels.join(' · ') : 'School header and details'
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  style,
}: {
  label: string
  sortKey: SortKey
  sort: SortState
  onSort: (key: SortKey) => void
  style?: React.CSSProperties
}) {
  const active = sort?.key === sortKey
  const dir = active ? sort.dir : null
  return (
    <th style={style}>
      <button
        type="button"
        className={`tc-sort-header ${active ? 'is-active' : ''}`}
        aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <span className="tc-sort-arrows" aria-hidden="true">
          <Icon.chevronUp size={12} className={`tc-sort-arrow ${dir === 'asc' ? 'is-active' : ''}`} />
          <Icon.chevronDown size={12} className={`tc-sort-arrow ${dir === 'desc' ? 'is-active' : ''}`} />
        </span>
      </button>
    </th>
  )
}
